import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Row,
  FormFeedback,
} from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import MediaModal from "../../PagesSection/Component/MediaModal";
import { useFormik } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import Messageservice from "../../../services/KangarooServices/MessageService";
import { imageBaseApi } from "../../../utilis/imageHelper";

// VALIDATION SCHEMA
const ceoMessageValidationSchema = Yup.object().shape({
  heading: Yup.string()
    .required("Heading is required")
    .min(5, "Heading must be at least 5 characters")
    .max(200, "Heading must not exceed 200 characters"),

  subHeading: Yup.string()
    .required("Sub heading is required")
    .min(5, "Sub heading must be at least 5 characters"),
  // .max(200, "Sub heading must not exceed 200 characters"),

  message: Yup.string()
    .required("Message is required")
    .min(20, "Message must be at least 20 characters"),

  ceoImageId: Yup.string().required("CEO image is required"),
});

const CEOMessageForm = () => {
  const { id } = useParams(); // For edit mode
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const [isMediaOpen, setIsMediaOpen] = useState(false);
  const toggleMedia = () => setIsMediaOpen(!isMediaOpen);

  // FORMIK SETUP
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      heading: "",
      subHeading: "",
      message: "",
      ceoImageId: "",
      ceoImage: null,
    },
    validationSchema: ceoMessageValidationSchema,
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: async (values) => {
      setIsSubmitting(true);

      // Clean payload
      const payload = {
        heading: values.heading,
        ceoImageId: values.ceoImageId,
        subHeading: values.subHeading,
        message: values.message,
      };

      try {
        if (id) {
          // UPDATE MODE
          await Messageservice.update(id, payload);
          toast.success("CEO Message updated successfully");
        } else {
          // CREATE MODE
          await Messageservice.create(payload);
          toast.success("CEO Message created successfully");
        }
        navigate("/message-from-ceo");
      } catch (err) {
        console.error("API Error:", err);

        // Handle specific error cases
        if (err.response?.status === 400) {
          toast.error("Invalid data. Please check all fields.");
        } else if (err.response?.status === 401) {
          toast.error("Unauthorized. Please login again.");
        } else if (err.response?.status === 404) {
          toast.error("Record not found.");
        } else if (err.response?.data?.message) {
          toast.error(err.response.data.message);
        } else {
          toast.error("Something went wrong! Please try again.");
        }
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    setFieldValue,
    setValues,
    handleChange,
    handleBlur,
    handleSubmit,
  } = formik;

  // FETCH DATA FOR EDIT MODE
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return; // Only fetch if ID exists (edit mode)

      setIsLoading(true);
      try {
        const response = await Messageservice.getbyId(id);

        // Map API response to form values
        setValues({
          heading: response.heading || "",
          subHeading: response.subHeading || "",
          message: response.message || "",
          ceoImageId: response.ceoImage?.id || "",
          ceoImage: response.ceoImage || null,
        });
      } catch (err) {
        console.error("Fetch Error:", err);
        toast.error("Failed to fetch data");
        navigate("/message-from-ceo");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, navigate, setValues]);

  // MEDIA MODAL HANDLER
  const handleMediaSelect = (media) => {
    setFieldValue("ceoImageId", media.id);
    setFieldValue("ceoImage", media);
    toggleMedia();
  };

  // FORM SUBMISSION WITH VALIDATION CHECK
  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Mark all fields as touched
    formik.setTouched({
      heading: true,
      subHeading: true,
      message: true,
      ceoImageId: true,
    });

    // Check for errors
    if (Object.keys(errors).length > 0) {
      toast.error("Please fix all validation errors before submitting");
      return;
    }

    handleSubmit();
  };

  // Show loading state while fetching data
  if (isLoading) {
    return (
      <div className="page-content">
        <Container fluid>
          <BreadCrumb pageTitle="CEO Message" title={id ? "Edit" : "Create"} />
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2">Loading...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb pageTitle="CEO Message" title={id ? "Edit" : "Create"} />
        <form onSubmit={handleFormSubmit}>
          <div className="d-flex justify-content-end gap-3 mb-3">
            <button
              type="submit"
              className="btn btn-success"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  {id ? "Updating..." : "Creating..."}
                </>
              ) : id ? (
                "Update"
              ) : (
                "Create"
              )}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => navigate("/message-from-ceo")}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>

          <Row>
            <Col lg={8}>
              <Card>
                <CardHeader className="fw-bold">CEO Message Details</CardHeader>
                <CardBody>
                  <FormGroup>
                    <Label>
                      Heading <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="heading"
                      placeholder="Enter Heading"
                      value={values.heading}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.heading && !!errors.heading}
                      disabled={isSubmitting}
                    />
                    {touched.heading && errors.heading && (
                      <FormFeedback>{errors.heading}</FormFeedback>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Sub Heading <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="subHeading"
                      placeholder="Enter Sub Heading"
                      value={values.subHeading}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.subHeading && !!errors.subHeading}
                      disabled={isSubmitting}
                    />
                    {touched.subHeading && errors.subHeading && (
                      <FormFeedback>{errors.subHeading}</FormFeedback>
                    )}
                  </FormGroup>

                  <FormGroup>
                    <Label>
                      Message <span className="text-danger">*</span>
                    </Label>
                    <CKEditor
                      editor={ClassicEditor}
                      data={values.message}
                      onChange={(event, editor) => {
                        const data = editor.getData();
                        setFieldValue("message", data);
                      }}
                      onBlur={() => formik.setFieldTouched("message", true)}
                      disabled={isSubmitting}
                    />
                    {touched.message && errors.message && (
                      <div className="text-danger small mt-1">
                        {errors.message}
                      </div>
                    )}
                  </FormGroup>
                </CardBody>
              </Card>
            </Col>

            <Col lg={4}>
              <Card>
                <CardHeader className="fw-bold">
                  CEO Image <span className="text-danger">*</span>
                </CardHeader>
                <CardBody>
                  {values.ceoImage ? (
                    <div className="mt-2 text-center">
                      {/* Image Preview */}
                      <div
                        style={{
                          maxHeight: "220px",
                          overflow: "hidden",
                          borderRadius: "10px",
                          border: "1px solid #ddd",
                        }}
                        className="mb-3"
                      >
                        <img
                          src={
                            values.ceoImage?.url
                              ? `${imageBaseApi}${values.ceoImage.url}`
                              : ""
                          }
                          alt={values.ceoImage.fileName || "CEO Image"}
                          style={{
                            maxHeight: "200px",
                            maxWidth: "100%",
                            borderRadius: "6px",
                            objectFit: "cover",
                          }}
                        />
                      </div>

                      {/* Media Info */}
                      <div className="mb-2">
                        <div className="fw-semibold">
                          {values.ceoImage.name || "CEO Image"}
                        </div>

                        {values.ceoImage.size && (
                          <small className="text-muted">
                            Size: {(values.ceoImage.size / 1024).toFixed(1)} KB
                          </small>
                        )}
                      </div>

                      {/* Edit/Delete buttons */}
                      <div className="d-flex gap-2 mt-2 justify-content-center">
                        <Button
                          outline
                          onClick={toggleMedia}
                          size="sm"
                          disabled={isSubmitting}
                        >
                          <i className="ri-edit-line"></i> Change
                        </Button>
                        <Button
                          outline
                          size="sm"
                          color="danger"
                          onClick={() => {
                            setFieldValue("ceoImageId", "");
                            setFieldValue("ceoImage", null);
                          }}
                          disabled={isSubmitting}
                        >
                          <i className="ri-delete-bin-5-line"></i> Remove
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 border rounded text-center bg-light">
                      <i className="ri-image-add-line fs-1 text-secondary"></i>
                      <h6 className="mt-2 mb-1 text-muted">
                        No CEO image selected
                      </h6>
                      <h6 className="mb-2 text-muted">800 x 800</h6>
                      <Button
                        outline
                        color="secondary"
                        className="rounded-pill px-4"
                        onClick={toggleMedia}
                        disabled={isSubmitting}
                      >
                        <i className="ri-image-add-line me-1"></i> Choose Image
                      </Button>
                    </div>
                  )}

                  {errors.ceoImageId && (
                    <div className="text-danger small mt-2 text-center">
                      {errors.ceoImageId}
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>

        <MediaModal
          isOpen={isMediaOpen}
          toggle={toggleMedia}
          selectedMedia={null}
          setSelectedMedia={handleMediaSelect}
        />
      </Container>
    </div>
  );
};

export default CEOMessageForm;
